/**
 * "Zahlungseingänge" — dated receipts on the `payment_date` axis.
 *
 * A DIFFERENT QUESTION FROM "BEZAHLT".
 * The KPI row's "Bezahlt (Stand heute)" answers "how much of the selected rent
 * month's cohort is paid by now". This card answers "how much money ARRIVED",
 * and the two are only loosely related — a payment landing today can belong to
 * a rent month from last year. That is why the cash figure is captioned here
 * rather than sitting in the KPI row next to its near-namesake.
 *
 * THE SERIES IS SPARSE AND STAYS SPARSE.
 * `cashflow_trend` holds only months with at least one TRUSTED payment. A
 * missing month means "we cannot vouch for anything here", not "CHF 0" — the
 * same class of claim as the pre-CURRENCY-1 default that assumed unverified
 * invoices were francs. Filling a gap with zero would manufacture that claim,
 * and cumulating over the filled series would bake it into every later point.
 * So: one bar per month the backend actually returned, no zero-filling, no
 * running total.
 *
 * NOT BUILT, AND NOT BECAUSE IT WAS FORGOTTEN:
 * - A daily axis. `cashflow_trend` is a monthly list; no daily series exists in
 *   the contract, and inventing one would invent a shape the truth owner never
 *   offered.
 * - A prior-year comparison line. `history_coverage.prior_year_comparison_available`
 *   is a CONSTANT false — payment completeness has never been established,
 *   because payments are only fetched for invoices inside the invoice sync
 *   delta. The card prints the backend's own reason instead of a dashed line.
 */
import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import {
  compactAxisTick,
  fmtMoney,
  periodLabel,
  SERIES_COLOR,
  shortPeriodLabel,
  toChartNumber,
} from "./revenueAnalyticsFormat";

function CashTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-foreground">{p.label}</div>
      <div className="text-muted-foreground">
        {fmtMoney(p.raw)} · {p.count} {p.count === 1 ? "Zahlung" : "Zahlungen"}
      </div>
    </div>
  );
}

export default function RevenueCashflowCard({ cashflow, cashflowTrend, selectedPeriod }) {
  const months = Array.isArray(cashflowTrend) ? cashflowTrend : [];
  const data = months.map((m) => ({
    label: shortPeriodLabel(m),
    raw: m?.received,
    received: toChartNumber(m?.received),
    count: Number(m?.payment_count ?? 0),
  }));

  const coverage = cashflow?.history_coverage;
  const trust = cashflow?.trust_summary;

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">Zahlungseingänge</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Nach Zahlungsdatum — nicht nach Mietmonat.
      </p>

      <div className="mt-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-sky-700 dark:text-sky-400">
          Zahlungseingänge im gewählten Monat
        </div>
        <div className="mt-1 text-2xl font-bold text-foreground">
          {fmtMoney(cashflow?.received_in_selected_period)}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Seit Jahresbeginn bis Ende {periodLabel(selectedPeriod)}:{" "}
          {fmtMoney(cashflow?.ytd_through_selected_month)}
        </div>
      </div>

      {data.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Es liegen keine verifizierten Zahlungseingänge vor.
        </p>
      ) : (
        <>
          <div className="mt-3 h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid
                  stroke="currentColor"
                  className="text-border"
                  strokeDasharray="3 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <YAxis
                  tickFormatter={compactAxisTick}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <Tooltip content={<CashTooltip />} cursor={false} />
                <Bar
                  dataKey="received"
                  name="Zahlungseingang"
                  fill={SERIES_COLOR.cash}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="sr-only">
            <caption>Verifizierte Zahlungseingänge je Monat</caption>
            <thead>
              <tr>
                <th scope="col">Monat</th>
                <th scope="col">Betrag</th>
                <th scope="col">Zahlungen</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.label}>
                  <th scope="row">{d.label}</th>
                  <td>{fmtMoney(d.raw)}</td>
                  <td>{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Nur Monate mit mindestens einem verifizierten Zahlungseingang. Eine Lücke ist keine
        bestätigte Null.
      </p>

      {coverage && coverage.prior_year_comparison_available !== true ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Kein Vorjahresvergleich: {coverage.prior_year_unavailable_reason || "Vollständigkeit der Zahlungshistorie nicht bestätigt"}.
        </p>
      ) : null}

      {trust ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Verifiziert: {trust.trusted_payment_count} Zahlungen ({fmtMoney(trust.trusted_amount)}) ·
          nicht berücksichtigt: {trust.excluded_payment_count} Zahlungen, davon in CHF{" "}
          {fmtMoney(trust.excluded_chf_amount_total)}.
        </p>
      ) : null}
    </div>
  );
}
